import "@saleor/utils/prototypeExtension/sortAlphabeticallyByKey";

import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUp from "@material-ui/icons/KeyboardArrowUp";
import { isAuthTokenExpired } from "@saleor/auth";
import {
  DateSelectionType,
  FIELD_TYPE,
  FORM_DATA,
  orderTypeChoices,
  SubmitFilterDataType
} from "@saleor/changeOrderExport/components/OrderFilterSection/constant";
import {
  ChangeOrderStatusEnum,
  ScgpExportOrderStatus,
  useListSourceOfAppQuery,
  useScgpMeQuery,
  useSuggestionShipToChangeOrderSearchLazyQuery,
  useSuggestionSoldToChangeOrderSearchLazyQuery
} from "@saleor/graphql";
import { useFormik } from "formik";
import moment from "moment-timezone";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";

const useOrderFilter = ({ handleChangeFilter }) => {
  const { data: dataScgpMe } = useScgpMeQuery({
    skip: isAuthTokenExpired()
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      piNo: "",
      eoNo: "",
      poNo: "",
      statusSAP: "",
      status: [],
      bu: dataScgpMe?.scgpMe?.extendData?.scgpBus[0]?.code || "",
      company:
        dataScgpMe?.scgpMe?.extendData?.scgpSalesOrganizations?.length === 1
          ? dataScgpMe.scgpMe.extendData.scgpSalesOrganizations[0].code
          : "All",
      soldTo: "",
      shipTo: "",
      dateSelectionType: DateSelectionType.CREATE_DATE,
      gte: null,
      lte: null,
      createDate: null,
      lastUpdate: null,
      sourceOfApp: "All"
    },
    onSubmit: data => {
      const {
        piNo,
        eoNo,
        poNo,
        bu,
        company,
        sourceOfApp,
        dateSelectionType
      } = data;
      if (!bu) {
        formik.setFieldError("bu", "โปรดระบุ BU");
      }
      if (!company) {
        formik.setFieldError("company", "โปรดระบุ Company");
      }
      if (!sourceOfApp) {
        formik.setFieldError("sourceOfApp", "โปรดระบุ Source Of App");
      }
      if (!dateSelectionType) {
        formik.setFieldError("dateSelectionType", "โปรดระบุ Date Range");
      }
      if (
        !piNo &&
        !eoNo &&
        !poNo &&
        !(formik.values.gte && formik.values.lte)
      ) {
        formik.setErrors({
          piNo: "กรุณากรอกข้อมูล",
          eoNo: "กรุณากรอกข้อมูล",
          poNo: "กรุณากรอกข้อมูล",
          gte: "กรุณากรอกข้อมูล",
          lte: "กรุณากรอกข้อมูล"
        });
      } else {
        const newData = { ...data };
        delete newData.statusSAP;
        delete newData.gte;
        delete newData.lte;

        const submitData: SubmitFilterDataType = { ...newData };
        delete submitData.dateSelectionType;
        submitData.statusSap = data.statusSAP as ChangeOrderStatusEnum;
        for (const [key, value] of Object.entries(submitData)) {
          if (!value) {
            delete submitData[key];
          }
        }
        const dateRange = {
          gte: data.gte ? moment(data.gte).format("YYYY-MM-DD") : undefined,
          lte: data.lte ? moment(data.lte).format("YYYY-MM-DD") : undefined
        };
        if (data.dateSelectionType === DateSelectionType.CREATE_DATE) {
          delete submitData.lastUpdate;
          submitData.createDate = dateRange;
        } else {
          delete submitData.createDate;
          submitData.lastUpdate = dateRange;
        }

        handleChangeFilter(submitData);
      }
    }
  });

  const intl = useIntl();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isClean, setIsClean] = useState<boolean>(false);

  const [
    getSoldToDataSuggestions,
    { data: fetchSoldToDataSuggestions, loading: loadingSoldToDataSuggestion }
  ] = useSuggestionSoldToChangeOrderSearchLazyQuery();

  const [
    getShipToDataSuggestions,
    { data: fetchShipToDataSuggestions, loading: loadingShipToDataSuggestion }
  ] = useSuggestionShipToChangeOrderSearchLazyQuery();

  const buList = useMemo(() => {
    const scgpBusList = [...(dataScgpMe?.scgpMe?.extendData?.scgpBus ?? [])];

    return scgpBusList.sortAlphabeticallyByKey("name").map(bu => ({
      label: bu.name,
      value: bu.name
    }));
  }, [dataScgpMe?.scgpMe?.extendData?.scgpBus]);

  const ArrowIconComponent = useMemo(
    () => (isOpen ? KeyboardArrowUp : KeyboardArrowDown),
    [isOpen]
  );
  const { data: sourceOfApp } = useListSourceOfAppQuery();
  const sourceOfAppListChange = useMemo(
    () =>
      sourceOfApp?.listSourceOfApp.map(({ value }) => ({
        label: value,
        value
      })),
    [sourceOfApp?.listSourceOfApp]
  );

  const handleCleanForm = useCallback(() => {
    setIsClean(true);
    handleChangeFilter({});
    setTimeout(() => {
      formik.resetForm();
      setIsClean(false);
    });
  }, []);

  const company =
    dataScgpMe?.scgpMe?.extendData?.scgpSalesOrganizationsAll.map(item => ({
      label: `${item.code} - ${item.name}`,
      value: item.code
    })) || [];

  const formData: FORM_DATA[] = useMemo(
    () => [
      {
        title: intl.formatMessage({ defaultMessage: "PI No." }),
        name: "piNo",
        placeholder: intl.formatMessage({ defaultMessage: "ระบุเลขที่ PI" }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 10
      },
      {
        title: intl.formatMessage({ defaultMessage: "EO No." }),
        name: "eoNo",
        placeholder: intl.formatMessage({ defaultMessage: "ระบุเลขที่ EO" }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 10
      },
      {
        title: intl.formatMessage({ defaultMessage: "PO No." }),
        name: "poNo",
        placeholder: intl.formatMessage({ defaultMessage: "ระบุเลขที่ PO" }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 35
      },
      {
        title: intl.formatMessage({ defaultMessage: "Order Status (SAP)" }),
        name: "statusSAP",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Order Status (SAP)"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: orderTypeChoices
      },
      {
        title: intl.formatMessage({ defaultMessage: "Sold to" }),
        name: "soldTo",
        placeholder: intl.formatMessage({ defaultMessage: "ค้นหา Sold to..." }),
        type: FIELD_TYPE.SUGGESTION_INPUT
      },
      {
        title: intl.formatMessage({ defaultMessage: "Ship to" }),
        name: "shipTo",
        placeholder: intl.formatMessage({ defaultMessage: "ค้นหา Ship to..." }),
        type: FIELD_TYPE.SUGGESTION_INPUT
      },
      {
        title: intl.formatMessage({ defaultMessage: "BU" }),
        name: "bu",
        placeholder: intl.formatMessage({ defaultMessage: "เลือก BU" }),
        type: FIELD_TYPE.SINGLE_SELECT,
        required: true,
        options: buList || []
      },
      {
        title: intl.formatMessage({ defaultMessage: "Order Status" }),
        name: "status",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Order Status"
        }),
        type: FIELD_TYPE.MULTIPLE_SELECT
      },
      {
        title: intl.formatMessage({ defaultMessage: "Company" }),
        name: "company",
        placeholder: intl.formatMessage({ defaultMessage: "เลือก Company" }),
        type: FIELD_TYPE.SINGLE_SELECT,
        required: true,
        options: [{ label: "All", value: "All" }, ...company],
        defaultValue:
          dataScgpMe?.scgpMe?.extendData?.scgpSalesOrganizations?.length === 1
            ? dataScgpMe?.scgpMe?.extendData?.scgpSalesOrganizations?.[0].code
            : "All"
      },
      {
        title: intl.formatMessage({ defaultMessage: "Date Range" }),
        name: "dateSelectionType",
        options: [
          {
            label: intl.formatMessage({ defaultMessage: "Create Date" }),
            value: DateSelectionType.CREATE_DATE
          },
          {
            label: intl.formatMessage({ defaultMessage: "Last Update Date" }),
            value: DateSelectionType.LAST_UPDATE
          }
        ],
        required: true,
        type: FIELD_TYPE.RADIO_GROUP
      },
      {
        title: intl.formatMessage({ defaultMessage: "Start Date" }),
        name: "gte",
        type: FIELD_TYPE.DATEPICKER,
        required:
          !formik.values.piNo && !formik.values.eoNo && !formik.values.poNo
      },
      {
        title: intl.formatMessage({ defaultMessage: "End Date" }),
        name: "lte",
        type: FIELD_TYPE.DATEPICKER,
        required:
          !formik.values.piNo && !formik.values.eoNo && !formik.values.poNo
      },
      {
        title: intl.formatMessage({ defaultMessage: "Source of App" }),
        name: "sourceOfApp",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Source of App"
        }),
        required: true,
        type: FIELD_TYPE.SINGLE_SELECT,
        options: sourceOfAppListChange || []
      }
    ],
    [
      buList,
      dataScgpMe,
      formik.values.piNo,
      formik.values.eoNo,
      formik.values.poNo,
      sourceOfAppListChange
    ]
  );

  const orderStatus = [
    {
      label: "Received Order",
      value: ScgpExportOrderStatus.RECEIVED_ORDER,
      color: "#A3AED0"
    },
    // {
    //   label: "Credit/Cash Issue",
    //   value: ScgpExportOrderStatus.CREDIT_CASH_ISSUE,
    //   color: "#FFC107"
    // },
    {
      label: "Partial Committed Order",
      value: ScgpExportOrderStatus.PARTIAL_COMMITTED_ORDER,
      color: "#3860BE"
    },
    {
      label: "Ready for delivery",
      value: ScgpExportOrderStatus.READY_FOR_DELIVERY,
      color: "#007BFF"
    },
    {
      label: "Partial Delivery",
      value: ScgpExportOrderStatus.PARTIAL_DELIVERY,
      color: "#009FB8"
    },
    {
      label: "Completed Delivery ",
      value: ScgpExportOrderStatus.COMPLETED_DELIVERY,
      color: "#6CC04A"
    },
    {
      label: "Cancelled ",
      value: ScgpExportOrderStatus.CANCELLED,
      color: "#E41B17"
    },
    {
      label: "Completed Order ",
      value: ScgpExportOrderStatus.COMPLETED_ORDER,
      color: "#1E7D34"
    }
  ];

  const setDataFormik = data => {
    formik.setValues(data);
  };

  return useMemo(
    () => ({
      formik,
      setDataFormik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      fetchSoldToDataSuggestions,
      loadingSoldToDataSuggestion,
      fetchShipToDataSuggestions,
      getSoldToDataSuggestions,
      getShipToDataSuggestions,
      loadingShipToDataSuggestion,
      listBU: buList,
      ArrowIconComponent,
      handleCleanForm,
      formData,
      orderStatus
    }),
    [
      formik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      fetchSoldToDataSuggestions,
      loadingSoldToDataSuggestion,
      fetchShipToDataSuggestions,
      getSoldToDataSuggestions,
      getShipToDataSuggestions,
      loadingShipToDataSuggestion,
      buList,
      ArrowIconComponent,
      handleCleanForm,
      formData,
      orderStatus
    ]
  );
};

export default useOrderFilter;
